import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import { PropertyUnitType } from '../../../../interfaces/property.interface';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';

interface Props {
  data: PropertyUnitType[];
  onChange: (data: PropertyUnitType[]) => void;
  view?: boolean;
  propertyTypes: any[];
  allPropertyTypes: any[];
  bhkTypes: any[];
  allBhkTypes: any[];
  measurements: any[];
  allMeasurements: any[];
  errors?: any;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
}

const InventoryDetailsTable: React.FC<Props> = ({
  data,
  onChange,
  view,
  propertyTypes,
  allPropertyTypes,
  bhkTypes,
  allBhkTypes,
  measurements,
  allMeasurements,
  errors,
  dropdownSearchData,
  onDropdownSearchChange,
}) => {
  const { t } = useTranslation();

  const handleAddRow = () => {
    const newRow: PropertyUnitType = {
      property_type_id: null,
      bhk_type: null,
      total_units: 0,
      price: '', // Changed to string to match input, can be parsed later
      rera_area: '',
      measurement: null,
      super_built_up_area: '',
    };
    onChange([...data, newRow]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedData = [...data];
    updatedData.splice(index, 1);
    onChange(updatedData);
  };

  const handleInputChange = (index: number, field: keyof PropertyUnitType, value: any) => {
    const updatedData = [...data];
    updatedData[index] = { ...updatedData[index], [field]: value };
    onChange(updatedData);
  };

  const handleReraAreaChange = (index: number, value: string) => {
    if (value === '' || /^\d{0,8}(\.\d{0,2})?$/.test(value)) {
      handleInputChange(index, 'rera_area', value);
    }
  };

  const handleSuperBuiltUpAreaChange = (index: number, value: string) => {
    if (value === '' || /^\d{0,8}(\.\d{0,2})?$/.test(value)) {
      handleInputChange(index, 'super_built_up_area', value);
    }
  };

  const propertyTypeOptions = useMemo(() => {
    return (propertyTypes || []).map((item: any) => ({
      label: item.name || item.value,
      value: String(item.id),
    }));
  }, [propertyTypes]);

  const bhkTypeOptions = useMemo(() => {
    return (bhkTypes || []).map((item: any) => ({
      label: item.value || item.name,
      value: String(item.id),
    }));
  }, [bhkTypes]);

  const allPropertyTypeOptions = useMemo(() => {
    return allPropertyTypes?.map((item: any) => ({
      label: item.name || item.value,
      value: String(item.id),
    }));
  }, [allPropertyTypes]);

  const allBhkTypeOptions = useMemo(() => {
    return allBhkTypes?.map((item: any) => ({
      label: item.value || item.name,
      value: String(item.id),
    }));
  }, [allBhkTypes]);

  const measurementOptions = useMemo(() => {
    return (measurements || []).map((item: any) => ({
      label: item.value || item.name,
      value: String(item.id),
    }));
  }, [measurements]);

  const allMeasurementOptions = useMemo(() => {
    return allMeasurements?.map((item: any) => ({
      label: item.value || item.name,
      value: String(item.id),
    }));
  }, [allMeasurements]);


  return (
    <div className="table-responsive text-nowrap mt-3">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ minWidth: '150px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.propertyType')}</th>
            <th style={{ minWidth: '120px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.bhkType')}</th>
            <th style={{ minWidth: '150px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.price')}</th>
            <th style={{ minWidth: '150px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.reraArea')}</th>
            <th style={{ minWidth: '150px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.measurement')}</th>
            <th style={{ minWidth: '150px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.superBuiltUpArea')}</th>
            <th style={{ minWidth: '100px' }}>{t('properties.addEdit.pageTexts.form.inventoryDetails.totalUnits')}</th>
            {!view && <th>{t('common.buttons.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>
                <Dropdown
                   id={`${VALIDATION_FIELD_MAPPING.property_type_id}-${index}`}
                   name={VALIDATION_FIELD_MAPPING.property_type_id}
                   values={row.property_type_id ? [String(row.property_type_id)] : []}
                   options={propertyTypeOptions}
                   allOptions={allPropertyTypeOptions}
                   onChange={(e) => handleInputChange(index, 'property_type_id', e.target.value)}
                   disabled={view}
                   placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.propertyType')}
                   label=""
                   error={!!errors?.[`property_type_id-${index}`]}
                   errorMessage={errors?.[`property_type_id-${index}`]}
                   onSearch={(e) => onDropdownSearchChange(e, 'property_type')}
                   searchText={dropdownSearchData.property_type}
                />
              </td>
              <td>
                 <Dropdown
                   id={`${VALIDATION_FIELD_MAPPING.bhk_type}-${index}`}
                   name={VALIDATION_FIELD_MAPPING.bhk_type}
                   values={row.bhk_type ? [String(row.bhk_type)] : []}
                   options={bhkTypeOptions}
                   allOptions={allBhkTypeOptions}
                   onChange={(e) => handleInputChange(index, 'bhk_type', e.target.value)}
                   disabled={view}
                   placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.bhkType')}
                   label=""
                   error={!!errors?.[`bhk_type-${index}`]}
                   errorMessage={errors?.[`bhk_type-${index}`]}
                   onSearch={(e) => onDropdownSearchChange(e, 'bhk_type')}
                   searchText={dropdownSearchData.bhk_type}
                />
              </td>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.price}-${index}`}
                  value={row.price}
                  onChange={(e) => handleInputChange(index, 'price', e.target.value)}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.price')}
                  type="number"
                  error={!!errors?.[`price-${index}`]}
                  errorMessage={errors?.[`price-${index}`]}
                />
              </td>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.rera_area}-${index}`}
                  value={row.rera_area}
                  onChange={(e) => handleReraAreaChange(index, e.target.value)}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.reraArea')}
                  error={!!errors?.[`rera_area-${index}`]}
                  errorMessage={errors?.[`rera_area-${index}`]}
                />
              </td>
              <td>
                 <Dropdown
                   id={`measurement-${index}`}
                   name="measurement"
                   values={row.measurement ? [String(row.measurement)] : []}
                   options={measurementOptions}
                   allOptions={allMeasurementOptions}
                   onChange={(e) => handleInputChange(index, 'measurement', e.target.value)}
                   disabled={view}
                   placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.measurement')}
                   label=""
                   error={!!errors?.[`measurement-${index}`]}
                   errorMessage={errors?.[`measurement-${index}`]}
                   onSearch={(e) => onDropdownSearchChange(e, 'measurement')}
                   searchText={dropdownSearchData.measurement}
                />
              </td>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.super_built_up_area}-${index}`}
                  value={row.super_built_up_area || ''}
                  onChange={(e) => handleSuperBuiltUpAreaChange(index, e.target.value)}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.superBuiltUpArea')}
                  error={!!errors?.[`super_built_up_area-${index}`]}
                  errorMessage={errors?.[`super_built_up_area-${index}`]}
                />
              </td>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.total_units}-${index}`}
                  value={row.total_units || ''}
                  onChange={(e) => handleInputChange(index, 'total_units', Number(e.target.value))}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.totalUnits')}
                  type="number"
                  error={!!errors?.[`total_units-${index}`]}
                  errorMessage={errors?.[`total_units-${index}`]}
                />
              </td>
              {!view && (
                <td className="text-center">
                  <Button
                    className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
                    onClick={() => handleRemoveRow(index)}
                    icon={<i className="ri-delete-bin-line"></i>}
                  />
                </td>
              )}
            </tr>
          ))}
          {!view && (
            <tr>
              <td colSpan={7}>
                <Button
                  className="btn btn-sm btn-primary"
                  onClick={handleAddRow}
                  text={t('common.buttons.add')}
                  icon={<i className="ri-add-line me-1"></i>}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InventoryDetailsTable;
